import { useChallengeRunStore } from "../state/challengeRunStore";
import { useChallengeStore } from "../state/challengeStore";
import { HeaderStats } from "./AppHeader";

/**
 * Split out of ChallengeView.tsx (GRA code-splitting pass) so App's header
 * stats row can import just the progress readout, not the whole Daily
 * challenge view (result card rendering, share image, Standing, TrendView):
 * this piece is needed the moment the challenge Loop is active, the rest
 * only once the run ends.
 */

/** One square per Round: how the run has gone, and where in it we are. */
function ProgressSquares({
  outcomes,
  total,
  current,
}: {
  outcomes: boolean[];
  total: number;
  current: number;
}) {
  return (
    <span className="flex items-center gap-[3px]" aria-hidden="true">
      {Array.from({ length: total }, (_, i) => {
        const outcome = outcomes[i];
        const tone =
          outcome === true
            ? "bg-up"
            : outcome === false
            ? "bg-down"
            : i === current
            ? "bg-accent/60"
            : "bg-muted/25";
        return <span key={i} className={`h-2 w-2 rounded-[2px] ${tone}`} />;
      })}
    </span>
  );
}

/**
 * The challenge's header readout, in the slot a Loop's stats row occupies
 * (GRA-94): the day, how the run has gone so far, and whether this attempt
 * counts. Deliberately not a hit rate: the run's score is only meaningful
 * once all ten are in, and the end screen is where it is announced.
 */
export function ChallengeProgress() {
  const date = useChallengeRunStore((s) => s.date);
  const total = useChallengeRunStore((s) => s.total);
  const outcomes = useChallengeRunStore((s) => s.outcomes);
  const practice = useChallengeRunStore((s) => s.practice);
  const runOver = useChallengeStore((s) => s.runOver);
  const phase = useChallengeStore((s) => s.phase);

  if (!date) return <HeaderStats>{null}</HeaderStats>;

  const done = outcomes.length;
  // The counter names the Round on screen, so it can never run ahead of what
  // the player has actually seen (GRA-145). The run advances at the guess,
  // not at Next, so during a reveal `done` already counts the Round still on
  // screen: the number holds there and moves on at Next.
  const shown = runOver
    ? total
    : Math.min(phase === "revealed" ? done : done + 1, total);
  return (
    <HeaderStats>
      <span className="text-dim">{date}</span>
      <span className="flex items-center gap-2">
        <ProgressSquares
          outcomes={outcomes}
          total={total}
          current={shown - 1}
        />
        <span>
          <span className="text-fg">{shown}</span>
          <span className="text-dim">/{total}</span>
        </span>
      </span>
      {practice && <span className="text-accent">practice</span>}
    </HeaderStats>
  );
}
